import React from "react";
import { TrendingUp, ArrowUpRight, Quote } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import { SectionContainer, SectionLabel, SectionHeading, SectionLead } from "./SectionPrimitives";

const CASES = [
  {
    code: "CASE-01",
    type: "Multi-Brand-Händler · 1.840 Fahrzeuge",
    title: "Von 53% Feed-Score auf 97% in 4 Wochen",
    metric1: { v: "+42%", l: "Klick-Reichweite" },
    metric2: { v: "−81%", l: "Disapprovals" },
    metric3: { v: "1.840", l: "SEO-Pages Live" },
    summary:
      "Multi-Brand-Händler mit chaotischem DMS-Export. Wir haben Quellen vereinheitlicht, Pflichtfelder aufgesetzt und in vier Wochen den kompletten Bestand live geschaltet.",
    quote:
      "Erstmals sehen wir, welche Fahrzeuge tatsächlich performen — und können den Vertrieb gezielt drauf ausrichten.",
    author: "Marketingleitung · Premium-Händler Süddeutschland",
  },
  {
    code: "CASE-02",
    type: "Händlergruppe · 12 Standorte",
    title: "Zentrale Datenpipeline für 12 Standorte",
    metric1: { v: "+38%", l: "Sichtbarkeit" },
    metric2: { v: "−72%", l: "Manueller Aufwand" },
    metric3: { v: "5.214", l: "Indexierte URLs" },
    summary:
      "Gruppe mit zwölf Standorten, vier DMS-Systemen und fragmentierter Markenarchitektur. Wir haben alles auf eine zentrale Datenpipeline überführt und group-weites Reporting eingeführt.",
    quote:
      "Wir hatten zwölf Excel-Listen und drei Agenturen. Heute haben wir eine Pipeline und einen Ansprechpartner.",
    author: "Geschäftsführung · Händlergruppe NRW",
  },
  {
    code: "CASE-03",
    type: "GW-Spezialist · 620 Fahrzeuge",
    title: "Long-Tail-SEO bringt 2,4× mehr Leads",
    metric1: { v: "2.4×", l: "Qualifizierte Leads" },
    metric2: { v: "+62%", l: "Long-Tail-Traffic" },
    metric3: { v: "98%", l: "Datenqualitäts-Score" },
    summary:
      "GW-Spezialist ohne SEO-Strategie. Mit angereicherten Fahrzeugseiten, Schema-Markup und internen Marken-/Modell-Hubs haben wir den Long-Tail aktiviert.",
    quote:
      "Anfragen kommen jetzt mit konkreterem Such-Intent rein. Der Abschluss-Funnel ist deutlich kürzer.",
    author: "Verkaufsleitung · GW-Spezialist Hessen",
  },
];

export default function Cases() {
  return (
    <SectionContainer id="projekte">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <SectionLabel number="/ 09" label="Cases · Resultate" />
          <SectionHeading>Echte Bestände. Messbare Ergebnisse.</SectionHeading>
          <SectionLead>
            Drei Beispiele aus dem deutschsprachigen Raum — alle Ergebnisse aus den ersten
            12 Wochen Service-Setup, dokumentiert mit Vorher-/Nachher-Reportings.
          </SectionLead>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.08}>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
              <p className="label-mono text-slate-500">Aggregat aus aktiven Projekten</p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Stat v="48k+" l="Fahrzeuge live" />
                <Stat v="97%" l="Feed-Score Ø" />
                <Stat v="34" l="Aktive Mandate" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <StaggerGroup className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {CASES.map((c, idx) => (
          <StaggerItem key={c.code}>
            <article
              data-testid={`case-${idx}`}
              className="group flex h-full flex-col rounded-lg border border-slate-200 bg-white p-7 ring-soft transition-all hover:-translate-y-1 hover:border-slate-300"
            >
              <div className="flex items-center justify-between">
                <span className="label-mono text-slate-400">{c.code}</span>
                <TrendingUp className="size-4 text-cyan-600" />
              </div>
              <p className="mt-5 label-mono text-slate-500">{c.type}</p>
              <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-slate-900">
                {c.title}
              </h3>

              <div className="mt-6 grid grid-cols-3 gap-3 border-y border-slate-100 py-4">
                <Metric {...c.metric1} />
                <Metric {...c.metric2} />
                <Metric {...c.metric3} />
              </div>

              <p className="mt-5 text-sm leading-relaxed text-slate-600">{c.summary}</p>

              <figure className="mt-6 flex flex-1 flex-col border-t border-slate-100 pt-5">
                <Quote className="mb-2 size-4 text-cyan-500" />
                <blockquote className="text-sm italic leading-relaxed text-slate-700">
                  „{c.quote}"
                </blockquote>
                <figcaption className="mt-3 label-mono text-slate-400">— {c.author}</figcaption>
              </figure>

              <div className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-medium text-cyan-700">
                Vergleichbares Setup besprechen <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </article>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </SectionContainer>
  );
}

function Metric({ v, l }) {
  return (
    <div>
      <p className="font-display text-xl font-bold text-slate-900 tabular-nums">{v}</p>
      <p className="label-mono text-slate-500 mt-1 text-[10px]">{l}</p>
    </div>
  );
}

function Stat({ v, l }) {
  return (
    <div>
      <p className="font-display text-xl font-bold text-slate-900 tabular-nums">{v}</p>
      <p className="label-mono text-slate-500 mt-1">{l}</p>
    </div>
  );
}
